import React, { useMemo, useState, useEffect } from "react";
import { Plus, Trash2, Download, Loader2, Eye } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CAP_TYPES = ["FTP", "Stand-up", "Standard", "Screw Cap", "Nozzle", "Fez", "Super Flat", "Other"];
const TUBE_TYPES = ["ABL", "PBL"];

const money = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const digits = (s) => (String(s || "").match(/[\d.]+/g) || []).join("") || "";
const todayISO = () => new Date().toISOString();
const plusDaysISO = (d) => new Date(Date.now() + d * 86400000).toISOString();
const toDateInput = (iso) => { try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; } };

function newItem(lead = {}) {
  return {
    description: lead.product_requirement || "Laminated Tube",
    size_g: "", diameter_mm: digits(lead.diameter), length_mm: "",
    cap_type: "", tube_type: (lead.tube_type || "").toUpperCase().includes("PBL") ? "PBL" : (lead.tube_type ? "ABL" : ""),
    hsn: "3923", quantity: digits(lead.quantity), price_per_tube: "",
    colours: "", plate_charge_per_color: "", plate_charges: "",
  };
}

const itemPlate = (it) => {
  const per = Number(it.plate_charge_per_color) || 0;
  const col = Number(it.colours) || 0;
  return per && col ? per * col : (Number(it.plate_charges) || 0);
};

// Reusable Proforma Invoice form. Works with an optional lead (prefill + save to lead)
// or standalone (no lead -> lead-less endpoint). `onClose` shows a Cancel button when provided.
export default function ProformaForm({ lead, onClose }) {
  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const endpoint = lead?.id ? `/leads/${lead.id}/proforma/pdf` : `/proforma/pdf`;

  useEffect(() => {
    const l = lead || {};
    setPreviewUrl(null);
    setForm({
      pi_date: todayISO(),
      valid_till: plusDaysISO(14),
      buyer_name: l.contact_name || "",
      buyer_company: l.company_name || "",
      buyer_gstin: "",
      buyer_phone: l.phone || "",
      buyer_email: l.email || "",
      billing_address: l.city ? `${l.city}` : "",
      shipping_address: "",
      buyer_state: "",
      gst_rate: 18,
      tax_type: "auto",
      freight_mode: "actual",
      freight_text: "",
      freight_amount: "",
      payment_terms: "50% advance, balance before dispatch",
      delivery_time: "~15 days from artwork approval",
      notes: "",
    });
    setItems([newItem(l)]);
  }, [lead]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, newItem()]);
  const removeItem = (i) => setItems((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const totals = useMemo(() => {
    let line = 0, plate = 0;
    items.forEach((it) => {
      line += (Number(it.quantity) || 0) * (Number(it.price_per_tube) || 0);
      plate += itemPlate(it);
    });
    const taxable = line + plate;
    const rate = Number(form?.gst_rate) || 0;
    let tt = form?.tax_type || "auto";
    if (tt === "auto") {
      const s = (form?.buyer_state || "").toLowerCase();
      tt = !s ? "intra" : (s.includes("himachal") || s === "hp") ? "intra" : "inter";
    }
    const freightAmt = form?.freight_mode === "custom" ? (Number(form?.freight_amount) || 0) : 0;
    const freightLabel = form?.freight_mode === "included" ? "Included"
      : form?.freight_mode === "custom" ? ((form?.freight_text || "").trim() || (freightAmt ? `₹${money(freightAmt)}` : "—"))
      : "As per actual";
    const taxableAll = taxable + freightAmt;
    const tax = (taxableAll * rate) / 100;
    const cgst = tt === "intra" ? tax / 2 : 0;
    const igst = tt === "inter" ? tax : 0;
    const grand = taxableAll + tax;
    return { line, plate, taxable: taxableAll, freightAmt, freightLabel, rate, tt, cgst, igst, tax, grand: Math.round(grand) };
  }, [items, form]);

  const buildPayload = () => ({
    ...form,
    gst_rate: Number(form.gst_rate) || 0,
    freight_amount: Number(form.freight_amount) || 0,
    items: items.map((it) => ({
      ...it,
      quantity: Number(it.quantity) || 0,
      price_per_tube: Number(it.price_per_tube) || 0,
      colours: Number(it.colours) || 0,
      plate_charge_per_color: Number(it.plate_charge_per_color) || 0,
      plate_charges: Number(it.plate_charges) || 0,
    })),
  });

  const validate = () => {
    if (!items.some((it) => Number(it.quantity) > 0 && Number(it.price_per_tube) > 0)) {
      toast.error("Add at least one item with quantity and price");
      return false;
    }
    return true;
  };

  const fileName = () => `proforma-${(form.buyer_company || lead?.company_name || "invoice").replace(/\s+/g, "-")}.pdf`;

  const saveBlob = (blob) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = fileName();
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const download = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.post(`${endpoint}?save=true`, buildPayload(), { responseType: "blob" });
      saveBlob(res.data);
      toast.success("Proforma invoice downloaded");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate the invoice");
    } finally { setBusy(false); }
  };

  const preview = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await api.post(endpoint, buildPayload(), { responseType: "blob" });
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't generate the preview");
    } finally { setBusy(false); }
  };

  const downloadFromPreview = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${endpoint}?save=true`, buildPayload(), { responseType: "blob" });
      saveBlob(res.data);
      toast.success("Proforma invoice downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't download the invoice");
    } finally { setBusy(false); }
  };

  const backToEdit = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  if (!form) return null;

  return (
    <div>
      {previewUrl ? (
        <iframe
          title="proforma-preview"
          src={previewUrl}
          className="w-full h-[68vh] border border-slate-200 rounded-lg bg-slate-50"
          data-testid="pf-preview-frame"
        />
      ) : (
        <div className="space-y-5">
          {/* Dates */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">PI Date</Label>
              <Input type="date" value={toDateInput(form.pi_date)} onChange={(e) => set("pi_date", new Date(e.target.value).toISOString())} data-testid="pf-date" />
            </div>
            <div>
              <Label className="text-xs">Valid Till</Label>
              <Input type="date" value={toDateInput(form.valid_till)} onChange={(e) => set("valid_till", new Date(e.target.value).toISOString())} data-testid="pf-valid" />
            </div>
          </div>

          {/* Buyer */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Buyer details</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Company</Label><Input value={form.buyer_company} onChange={(e) => set("buyer_company", e.target.value)} data-testid="pf-company" /></div>
              <div><Label className="text-xs">Contact name</Label><Input value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} data-testid="pf-name" /></div>
              <div><Label className="text-xs">GSTIN</Label><Input value={form.buyer_gstin} onChange={(e) => set("buyer_gstin", e.target.value)} placeholder="Buyer GSTIN" data-testid="pf-gstin" /></div>
              <div><Label className="text-xs">State (for tax)</Label><Input value={form.buyer_state} onChange={(e) => set("buyer_state", e.target.value)} placeholder="e.g. Haryana" data-testid="pf-state" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} /></div>
              <div><Label className="text-xs">Email</Label><Input value={form.buyer_email} onChange={(e) => set("buyer_email", e.target.value)} /></div>
              <div><Label className="text-xs">Billing address</Label><Textarea rows={2} value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} data-testid="pf-billing" /></div>
              <div><Label className="text-xs">Shipping address (blank = same)</Label><Textarea rows={2} value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} /></div>
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-700">Line items</h4>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="pf-add-item" className="h-8 gap-1"><Plus className="h-3.5 w-3.5" /> Add item</Button>
            </div>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid={`pf-item-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-500">Item {i + 1}</span>
                    {items.length > 1 && (
                      <button onClick={() => removeItem(i)} data-testid={`pf-remove-${i}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="col-span-2 sm:col-span-4"><Label className="text-[11px]">Description</Label><Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} /></div>
                    <div><Label className="text-[11px]">Size (g)</Label><Input value={it.size_g} onChange={(e) => setItem(i, "size_g", e.target.value)} /></div>
                    <div><Label className="text-[11px]">Diameter (mm)</Label><Input value={it.diameter_mm} onChange={(e) => setItem(i, "diameter_mm", e.target.value)} /></div>
                    <div><Label className="text-[11px]">Length (mm)</Label><Input value={it.length_mm} onChange={(e) => setItem(i, "length_mm", e.target.value)} /></div>
                    <div>
                      <Label className="text-[11px]">Cap type</Label>
                      <Select value={it.cap_type || undefined} onValueChange={(v) => setItem(i, "cap_type", v)}>
                        <SelectTrigger className="h-9" data-testid={`pf-cap-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CAP_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px]">Tube type</Label>
                      <Select value={it.tube_type || undefined} onValueChange={(v) => setItem(i, "tube_type", v)}>
                        <SelectTrigger className="h-9" data-testid={`pf-tube-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{TUBE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-[11px]">HSN</Label><Input value={it.hsn} onChange={(e) => setItem(i, "hsn", e.target.value)} /></div>
                    <div><Label className="text-[11px]">Quantity</Label><Input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`pf-qty-${i}`} /></div>
                    <div><Label className="text-[11px]">Price / tube (₹)</Label><Input type="number" step="0.001" value={it.price_per_tube} onChange={(e) => setItem(i, "price_per_tube", e.target.value)} data-testid={`pf-price-${i}`} /></div>
                    <div><Label className="text-[11px]">No. of colours</Label><Input type="number" value={it.colours} onChange={(e) => setItem(i, "colours", e.target.value)} data-testid={`pf-colours-${i}`} /></div>
                    <div><Label className="text-[11px]">Plate chg / colour (₹)</Label><Input type="number" value={it.plate_charge_per_color} onChange={(e) => setItem(i, "plate_charge_per_color", e.target.value)} data-testid={`pf-plate-${i}`} /></div>
                    <div className="flex items-end"><span className="text-xs text-slate-500">Plate: <b className="text-slate-800">₹{money(itemPlate(it))}</b></span></div>
                    <div className="flex items-end"><span className="text-xs text-slate-500">Amount: <b className="text-slate-800">₹{money((Number(it.quantity) || 0) * (Number(it.price_per_tube) || 0))}</b></span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tax + terms */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">GST rate (%)</Label>
              <Input type="number" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} data-testid="pf-gst" />
            </div>
            <div>
              <Label className="text-xs">Tax type</Label>
              <Select value={form.tax_type} onValueChange={(v) => set("tax_type", v)}>
                <SelectTrigger data-testid="pf-taxtype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (by buyer state)</SelectItem>
                  <SelectItem value="intra">CGST + SGST (within HP)</SelectItem>
                  <SelectItem value="inter">IGST (other state)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Delivery time</Label>
              <Input value={form.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} />
            </div>
            <div className="sm:col-span-3"><Label className="text-xs">Payment terms</Label><Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} /></div>
            <div className="sm:col-span-3"><Label className="text-xs">Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>

          {/* Logistics / Freight */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Logistics / Freight</Label>
              <Select value={form.freight_mode} onValueChange={(v) => set("freight_mode", v)}>
                <SelectTrigger data-testid="pf-freight-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actual">As per actual</SelectItem>
                  <SelectItem value="included">Included in price</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.freight_mode === "custom" && (
              <>
                <div className="sm:col-span-1">
                  <Label className="text-xs">Freight note</Label>
                  <Input value={form.freight_text} onChange={(e) => set("freight_text", e.target.value)} placeholder="e.g. By road, to-pay" data-testid="pf-freight-text" />
                </div>
                <div>
                  <Label className="text-xs">Freight amount (₹, optional)</Label>
                  <Input type="number" value={form.freight_amount} onChange={(e) => set("freight_amount", e.target.value)} placeholder="Added to total if entered" data-testid="pf-freight-amount" />
                </div>
              </>
            )}
          </div>

          {/* Totals preview */}
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="pf-totals">
            <div className="space-y-1 text-sm max-w-xs ml-auto">
              <Row label="Sub Total (Goods)" value={totals.line} />
              <Row label="Plate / Tooling" value={totals.plate} />
              {totals.freightAmt > 0 ? (
                <Row label="Freight / Logistics" value={totals.freightAmt} />
              ) : (
                <div className="flex justify-between text-slate-600"><span>Freight / Logistics</span><span className="italic text-slate-500">{totals.freightLabel}</span></div>
              )}
              <Row label="Taxable Value" value={totals.taxable} />
              {totals.tt === "intra" ? (
                <>
                  <Row label={`CGST @ ${totals.rate / 2}%`} value={totals.cgst} />
                  <Row label={`SGST @ ${totals.rate / 2}%`} value={totals.cgst} />
                </>
              ) : (
                <Row label={`IGST @ ${totals.rate}%`} value={totals.igst} />
              )}
              <div className="flex justify-between border-t border-slate-300 pt-1 font-semibold text-slate-900">
                <span>Grand Total (INR)</span><span>₹{money(totals.grand)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        {previewUrl ? (
          <>
            <Button variant="outline" onClick={backToEdit} data-testid="pf-back-edit">Back to edit</Button>
            <Button onClick={downloadFromPreview} disabled={busy} data-testid="pf-download" className="bg-rose-600 hover:bg-rose-700 gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </Button>
          </>
        ) : (
          <>
            {onClose && <Button variant="outline" onClick={onClose}>Cancel</Button>}
            <Button variant="outline" onClick={preview} disabled={busy} data-testid="pf-preview" className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
            </Button>
            <Button onClick={download} disabled={busy} data-testid="pf-download-direct" className="bg-rose-600 hover:bg-rose-700 gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span><span>₹{money(value)}</span>
    </div>
  );
}
